import type { AuthUser } from "@models/quota";
import { useAuthStore } from "@stores/authStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { authApi, usersApi } from "../services/api";

export const authKeys = {
  me: ["auth", "me"] as const,
};

export function useCurrentUser() {
  const query = useQuery({
    queryKey: authKeys.me,
    queryFn: authApi.getMe,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  // Mirror RQ's authoritative result into the persisted store so
  // synchronous consumers (Sidebar, command palette) read a stable
  // value across remounts and so a fresh tab can hydrate the user
  // before /auth/me resolves. The store is read via getState() inside
  // the effect — the action ref is stable in Zustand and doesn't need
  // to live in the dep array or trigger rerenders of this hook.
  useEffect(() => {
    if (query.data) {
      useAuthStore.getState().setUser(query.data);
    } else if (query.isError) {
      useAuthStore.getState().setUser(null);
    }
  }, [query.data, query.isError]);

  return query;
}

/**
 * Mutate the caller's profile (currently: display-name override).
 * Pushes the server's authoritative response into the auth-me query
 * cache + Zustand store so every consumer (sidebar, command palette,
 * profile page) reflects the change immediately.
 */
export function useUpdateProfile() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: usersApi.updateProfile,
    onSuccess: (fresh: AuthUser) => {
      qc.setQueryData(authKeys.me, fresh);
      useAuthStore.getState().setUser(fresh);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      useAuthStore.getState().clearAuth();
      // Belt-and-braces: also explicitly wipe the persisted localStorage
      // entry. clearAuth() relies on the persist middleware writing the
      // cleared in-memory state out, which happens on the next event-loop
      // tick. If a refresh races between the mutation firing and that
      // tick (or if a stale tab in another window wakes up via the
      // storage event), a stale `user` value can flash on the next page
      // load. clearStorage() is synchronous and wipes the key cleanly.
      useAuthStore.persist.clearStorage();
      qc.clear();
      window.location.href = "/login";
    },
  });
}
